export const filterRemovedRows = <T>(rows: T[], removedIds: Set<string>, getRowId: (row: T) => string): T[] =>
  removedIds.size === 0 ? rows : rows.filter(row => !removedIds.has(getRowId(row)));

export const selectOptimisticWindow = <T>(
  fetchedRows: T[],
  removedIds: Set<string>,
  getRowId: (row: T) => string,
  sliceStart: number,
  sliceEnd: number,
  rawTotal: number
): { rows: T[]; rowCount: number } => {
  const survivors = filterRemovedRows(fetchedRows, removedIds, getRowId);
  return {
    rows: survivors.slice(sliceStart, sliceEnd),
    rowCount: Math.max(0, rawTotal - removedIds.size),
  };
};

export const backfillRemovedWindow = async <T, R extends Record<string, T[]>>(parameters: {
  firstPageResponse: R & { pageData: { total: number } };
  fetchPage: (page: number) => Promise<R & { pageData: { total: number } }>;
  dataKey: keyof R;
  removedIds: Set<string>;
  getRowId: (row: T) => string;
  firstRequestedPage: number;
  requestedStart: number;
  requestedEnd: number;
  pageSize: number;
}): Promise<{ rows: T[]; rowCount: number }> => {
  const {
    firstPageResponse,
    fetchPage,
    dataKey,
    removedIds,
    getRowId,
    firstRequestedPage,
    requestedStart,
    requestedEnd,
    pageSize,
  } = parameters;

  const rawTotal = firstPageResponse.pageData.total;
  const lastRawPage = Math.ceil(rawTotal / pageSize);

  // Once rows are optimistically removed the grid's rowCount shrinks, so MUI
  // requests start/end in survivor coordinates. Survivors come from the raw
  // stream with removed ids filtered out, so the window must be sliced from
  // survivors accumulated from the first page. The first `pagesToFetch` raw
  // pages always hold at least `requestedEnd + 1` survivors (each page loses at
  // most `removedIds.size` rows in total), so fetch exactly that many pages in
  // parallel rather than walking pages serially.
  const neededSurvivors = requestedEnd + 1;
  const pagesToFetch = Math.min(lastRawPage, Math.ceil((neededSurvivors + removedIds.size) / pageSize));
  const responses = await Promise.all(
    Array.from({ length: pagesToFetch }, (_, index) => index + 1).map(page =>
      page === firstRequestedPage ? Promise.resolve(firstPageResponse) : fetchPage(page)
    )
  );
  const fetchedRows = responses.flatMap(response => response[dataKey]);

  return selectOptimisticWindow(fetchedRows, removedIds, getRowId, requestedStart, requestedEnd + 1, rawTotal);
};
